import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, MapPin } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface PrayerTimesData {
  fajr: string;
  sunrise: string;
  dhuhr: string;
  asr: string;
  maghrib: string;
  isha: string;
  jummah: string;
  date: string;
  location: {
    latitude: number;
    longitude: number;
  };
}

interface PrayerTimesProps {
  onPrayerTimesLoaded?: (data: PrayerTimesData) => void;
}

const PrayerTimes = ({ onPrayerTimesLoaded }: PrayerTimesProps) => {
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [prayerTimes, setPrayerTimes] = useState<PrayerTimesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  const fetchPrayerTimes = async (lat: number, lng: number) => {
    try {
      const { data, error } = await supabase.functions.invoke('calculate-prayer-times', {
        body: {
          latitude: lat,
          longitude: lng,
          method: 'MWL'
        }
      });

      if (error) throw error;
      
      setPrayerTimes(data);
      onPrayerTimesLoaded?.(data);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching prayer times:', err);
      setError('Failed to calculate prayer times');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          setLocation({ lat, lng });
          fetchPrayerTimes(lat, lng);
        },
        (error) => {
          console.error("Error getting location:", error);
          setError("Unable to get your location");
          setLoading(false);
        }
      );
    } else {
      setError("Geolocation is not supported by this browser");
      setLoading(false);
    }
  }, []);

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  // Check if we need to fetch new prayer times (daily at midnight)
  useEffect(() => {
    if (!prayerTimes || !location) return;

    const checkForNewDay = () => {
      const now = new Date();
      const prayerDate = new Date(prayerTimes.date);
      
      // If it's a new day, fetch new prayer times
      if (now.toDateString() !== prayerDate.toDateString()) {
        fetchPrayerTimes(location.lat, location.lng);
      }
    };

    // Check every hour if we need new prayer times
    const dailyTimer = setInterval(checkForNewDay, 60 * 60 * 1000); // Every hour
    
    return () => clearInterval(dailyTimer);
  }, [prayerTimes, location]);

  if (loading) {
    return (
      <Card className="bg-gradient-to-br from-primary/5 to-primary/10">
        <CardHeader>
          <CardTitle className="text-primary flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Prayer Times
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <div className="animate-pulse">Loading prayer times...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="bg-gradient-to-br from-primary/5 to-primary/10">
        <CardHeader>
          <CardTitle className="text-primary flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Prayer Times
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-destructive">
            {error}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!prayerTimes) return null;

  const formattedCurrentTime = currentTime.toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  });
  const isJummahDay = currentTime.getDay() === 5; // Friday is day 5

  return (
    <div className="space-y-6">
      {/* Current Time & Date */}
      <Card className="p-6 bg-gradient-to-r from-primary/10 to-accent/10 border-primary/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Clock className="h-6 w-6 text-primary" />
            <div>
              <p className="text-sm text-muted-foreground">Current Time</p>
              <p className="text-2xl font-bold text-primary">{formattedCurrentTime}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-muted-foreground">Today's Date</p>
            <p className="text-lg font-semibold text-accent">{prayerTimes.date}</p>
          </div>
        </div>
      </Card>

      {/* Prayer Times Grid */}
      <Card className="bg-gradient-to-br from-primary/5 to-primary/10">
        <CardHeader>
          <CardTitle className="text-primary flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Today's Prayer Times
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(prayerTimes).map(([prayer, time]) => {
              if (prayer === 'date' || prayer === 'location' || prayer === 'jummah') return null;
              
              const prayerName = prayer.charAt(0).toUpperCase() + prayer.slice(1);
              
              return (
                <div 
                  key={prayer} 
                  className="text-center p-3 rounded-lg border bg-background/50"
                >
                  <div className="text-sm text-muted-foreground">{prayerName}</div>
                  <div className="font-mono text-lg font-bold text-emerald-800 dark:text-emerald-300">{time as string}</div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PrayerTimes;